// /frontend/src/main.ts

/**
 * Main entry point for the Vue application.
 * This file is responsible for creating the Vue app instance,
 * registering all essential plugins (Router, Pinia, Vuetify, i18n),
 * and mounting the application to the DOM.
 */

// --- 1. Core Vue Imports ---
import { createApp } from 'vue';

// --- 2. Root Component ---
import App from './App.vue';

// --- 3. Plugin Registration ---
import { registerPlugins } from './plugins';
import { registerChartJs } from './plugins/chartjs';

// --- 4. API Service Initialization ---
// We initialize the API service here to configure it application-wide (e.g., with base URL).
// This needs to be done before the app is mounted.
import { initApiService } from './services/api.service'; // We will create this service file

/**
 * Initializes the API service.
 * This function sets up the base configuration for all subsequent API calls,
 * such as the base URL from environment variables.
 *
 * @author Alexander Funke
 * @lastChange 2025-08-25
 */
function initializeServices(): void {
  // The backend URL is proxied via vite.config.mts in development.
  // In production, it might point to a different domain.
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
  initApiService(apiBaseUrl);
}

/**
 * Creates, configures, and mounts the Vue application.
 * This is the main bootstrap function.
 *
 * @author Alexander Funke
 * @lastChange 2025-08-25
 */
function bootstrap(): void {
  // First, initialize services that are needed application-wide.
  initializeServices();
  registerChartJs();

  // Create the Vue application instance.
  const app = createApp(App);

  // Register all plugins (Vue Router, Pinia, Vuetify, etc.).
  registerPlugins(app);

  // Mount the application to the DOM element with the id 'app'.
  app.mount('#app');
}

// --- 5. Start the Application ---
bootstrap();
